import { setConfiguration } from 'react-grid-system'

import {
  EMessageType,
  UniAuthManager,
  EAuthNoticeKind
} from '@unikey/unikey-commons/release/comm';

import {
  api,
  store,
  navConfig, ENavPages,
  oidcAuthority,
  oidcClientId,
  oidcClientSecret,
  oidcRedirectUri,
  oidcSilentRedirectUri,
  oidcScope,
  mockAuth,
  buildOidcAcrValues,
  isDevelopmentEnv,
  addAlert,
  redirectToLogin,
  redirectToLogout,
  addPendingRedirect
} from '../../internal';
import { tokenRefreshDealerPortalBusiness } from './userManagerBusiness';
import { registerOidcTeardown } from '../../helpers/redirectHelpers';

setConfiguration({
  maxScreenClass: 'xxxl',
  breakpoints: [576, 768, 992, 1200, 1660, 2410],
  containerWidths: [540, 740, 960, 1140, 1580, 1840],
  gridColumns: 24
});

// The OIDC orchestration (UserManager lifecycle, silent renew, refresh-error
// handling, the login-loop fixes and cross-tab single-flight) now lives in the
// shared UniAuthManager in commons. This module is a thin adapter: it supplies
// cdp's config and wires the portal-specific callbacks (redux dispatch, routing,
// the dealer/org business flow). See the shared class for the loop fixes.
export const authManager = new UniAuthManager({
  authority: oidcAuthority,
  clientId: oidcClientId,
  clientSecret: oidcClientSecret,
  redirectUri: oidcRedirectUri,
  silentRedirectUri: oidcSilentRedirectUri,
  scope: oidcScope,
  acrValues: buildOidcAcrValues(),
  prompt: 'login',
  automaticSilentRenew: !mockAuth,

  tokenSink: api,

  // account-verification gate + dealer/org selection stay in the portal
  onTokenApplied: async (decoded: any) => {
    const accountVerified = decoded?.account_verified ?? decoded?.email_verified;
    if (!accountVerified || accountVerified === 'false') {
      store?.dispatch(addPendingRedirect({
        pathname: navConfig.get(ENavPages.verifyLanding)!.linkTo([]),
        priority: 'high'
      }));
      return;
    }
    await tokenRefreshDealerPortalBusiness(decoded);
  },

  onRedirectToLogin: () => { store?.dispatch(redirectToLogin()); },
  onRedirectToLogout: () => { store?.dispatch(redirectToLogout()); },

  // only bounce to login from within the authenticated portal, so public pages
  // (version, legal, applinks, guides, etc.) aren't kicked to login.
  isInPortalContext: () => /\#\/portal/.test(window.location.hash),
  // skip redundant re-routing when app state already reflects the current user
  isSessionHydrated: () => !!store?.getState().authenticatedUser?.currentUser?.id,

  // dev-only toasts mirror the previous inline behavior; production relies on the
  // manager's always-on [auth] console trace instead.
  onNotice: isDevelopmentEnv
    ? (n) => {
        const type = n.kind === EAuthNoticeKind.RefreshError || n.kind === EAuthNoticeKind.Unrecoverable
          ? EMessageType.error
          : EMessageType.info;
        store?.dispatch(addAlert({
          id: Date.now(),
          titleKey: `(DEV) ${n.kind}`,
          messageKeys: [n.detail ?? ''],
          type,
          duration: 3000
        }));
      }
    : undefined,
});

// Let the app's central clearAllAuthValues() also tear down OIDC state, without
// redirectHelpers having to statically import this module (which would break the
// module-eval order — see registerOidcTeardown).
registerOidcTeardown(() => authManager.clearOidcState());

// The underlying oidc-client-ts UserManager, for <AuthProvider> / useAuth.
export const userManager = authManager.userManager;

// Preserved exports (RootContainer, Verify/ActionToken/AdminInvitation containers
// call these). They now delegate to the shared manager.
export const handleNewOrRefreshedToken = authManager.handleNewOrRefreshedToken;
export const requestRefreshToken = authManager.requestRefreshToken;
